import { useState, useMemo, useEffect, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  type ColumnDef,
  flexRender,
  type VisibilityState,
} from '@tanstack/react-table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../layouts';
import { api, type User, type UserStatus, type UserRole, type UserType } from '../lib/api';
import { hasRole, isSuperAdmin } from '../lib/auth';
import { Trash2, Check, X, Ban, Pencil, Settings } from 'lucide-react';

type CreateUserFormData = {
  name: string;
  organization: string;
  email: string;
  password: string;
  confirmPassword: string;
  roles: UserRole[];
  userType: UserType | '';
};

export const Users = () => {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [userTypeFilter, setUserTypeFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  // const [showEditRolesModal, setShowEditRolesModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [createFormData, setCreateFormData] = useState<CreateUserFormData>({
    name: '',
    organization: '',
    email: '',
    password: '',
    confirmPassword: '',
    roles: ['user'],
    userType: ''
  });
  // const [editRoles, setEditRoles] = useState<UserRole[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [rejectUser, setRejectUser] = useState<User | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [deleteUser, setDeleteUser] = useState<User | null>(null);

  // const currentUser = getCurrentUser();
  const canCreateUsers = hasRole('admin');
  // Admin (in addition to Super Admin) can edit users — Users is Admin's
  // one feature, so it should include full management of it.
  const canEditRoles = hasRole('admin');

  const availableRolesForCreate: UserRole[] = isSuperAdmin()
    ? ['user', 'expert', 'admin', 'super_admin']
    : ['user', 'expert'];

  // Admin can only assign the same roles it can create with (not admin/super_admin,
  // to avoid an Admin escalating a user's privileges) — Super Admin can assign any role.
  const availableRolesForEdit: UserRole[] = isSuperAdmin()
    ? ['user', 'expert', 'admin', 'super_admin']
    : ['user', 'expert'];

  const { data, isLoading } = useQuery<User[]>({
    queryKey: ['users', statusFilter, userTypeFilter],
    queryFn: () => {
      const status = statusFilter === 'all' ? undefined : statusFilter as UserStatus;
      const uType = userTypeFilter === 'all' ? undefined : userTypeFilter as UserType;
      return api.getUsers(status, uType);
    }
  });

  const [showEditUserModal, setShowEditUserModal] = useState<boolean>(false);

  const [editFormData, setEditFormData] = useState<{
    name: string;
    organization?: string;
    roles: UserRole[];
    userType: UserType;
  }>({
    name: '',
    organization: '',
    roles: [],
    userType: 'customer',
  });

  const [approveUser, setApproveUser] = useState<User | null>(null);
  const [approveUserType, setApproveUserType] = useState<UserType | null>(null);
  const [approveModalError, setApproveModalError] = useState<string | null>(null);

  const updateUserStatus = useMutation({
    mutationFn: ({
      id,
      status,
      userType,
      reason
    }: {
      id: string;
      status: UserStatus;
      userType?: UserType;
      reason?: string;
    }) => api.updateUserStatus(id, status, userType, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setApproveUser(null);
      setApproveUserType(null);
      setApproveModalError(null);
    },
    onError: (error: Error, variables) => {
      if (variables.status === 'approved') {
        setApproveModalError(error.message || 'Failed to approve user');
      }
    },
  });

  const createUserMutation = useMutation({
    mutationFn: (data: { name: string; organization: string; email: string; password: string; roles: UserRole[]; userType?: UserType }) =>
      api.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateModal(false);
      setCreateFormData({ name: '', organization: '', email: '', password: '', confirmPassword: '', roles: ['user'], userType: '' });
      setFormError(null);
    },
    onError: (error: Error) => {
      setFormError(error.message || 'Failed to create user');
    }
  });

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!createFormData.name.trim()) {
      setFormError('Full Name is required');
      return;
    }

    if (!createFormData.organization.trim()) {
      setFormError('Organization is required');
      return;
    }

    if (!createFormData.email.trim()) {
      setFormError('Email is required');
      return;
    }

    if (!createFormData.password) {
      setFormError('Password is required');
      return;
    }

    if (!createFormData.confirmPassword) {
      setFormError('Confirm Password is required');
      return;
    }

    if (createFormData.password !== createFormData.confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    if (createFormData.roles.length === 0) {
      setFormError('At least one role is required');
      return;
    }

    if (!createFormData.userType) {
      setFormError('User type is required');
      return;
    }

    createUserMutation.mutate({
      name: createFormData.name.trim(),
      organization: createFormData.organization.trim(),
      email: createFormData.email.trim(),
      password: createFormData.password,
      roles: createFormData.roles,
      userType: createFormData.userType,
    });
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      name: user.displayName || '',
      organization: user.organization || '',
      roles: user.roles || [],
      userType: user.userType || 'customer',
    });
    setFormError(null);
    setShowEditUserModal(true);
  };

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; organization?: string; roles: UserRole[]; userType?: UserType } }) =>
      api.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowEditUserModal(false);
      setEditingUser(null);
      setEditFormData({ name: '', organization: '', roles: [], userType: 'customer' });
      setFormError(null);
    },
    onError: (error: Error) => {
      setFormError(error.message || 'Failed to update user');
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteUser(null);
    }
  });

  const handleSaveUser = () => {
    if (!editingUser) return;

    if (!editFormData.name.trim()) {
      setFormError('Name is required');
      return;
    }

    if (editFormData.roles.length === 0) {
      setFormError('At least one role is required');
      return;
    }

    updateUserMutation.mutate({
      id: editingUser.id,
      data: {
        name: editFormData.name.trim(),
        organization: editFormData.organization?.trim(),
        roles: editFormData.roles,
        userType: editFormData.userType,
      }
    });
  };

  const toggleRole = (role: UserRole, isCreate: boolean) => {
    if (isCreate) {
      setCreateFormData(prev => ({
        ...prev,
        roles: prev.roles.includes(role)
          ? prev.roles.filter(r => r !== role)
          : [...prev.roles, role]
      }));
    } else {

      setEditFormData(prev => ({
        ...prev,
        roles: prev.roles.includes(role)
          ? prev.roles.filter(r => r !== role)
          : [...prev.roles, role]
      }));
    }
  };

  const roleLabels: Record<UserRole, string> = {
    user: 'User',
    expert: 'Expert',
    admin: 'Admin',
    super_admin: 'Super Admin',
  };

  const userTypeLabels: Record<UserType, string> = {
    employee: 'Employee',
    distributor: 'Distributor',
    customer: 'Customer',
  };

  const handleConfirmApprove = () => {
    if (!approveUser) return;
    if (!approveUserType) {
      setApproveModalError('Please select a user type');
      return;
    }
    setApproveModalError(null);
    updateUserStatus.mutate({
      id: approveUser.id,
      status: 'approved',
      userType: approveUserType,
    });
  };

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    name: true,
    organization: true,
    status: true,
    userType: true,
    actions: true,

    // hidden by default
    roles: false,
    createdAt: false,
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        columnMenuRef.current &&
        !columnMenuRef.current.contains(event.target as Node)
      ) {
        setShowColumnMenu(false);
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowColumnMenu(false);
      }
    };

    if (showColumnMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showColumnMenu]);

  const columns: ColumnDef<User>[] = [
    {
      header: 'User Name',
      accessorKey: 'name',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <span className="font-medium text-gray-900">{row.original.displayName}</span>
        </div>
      ),
    },
    {
      header: 'Organization',
      accessorKey: 'organization',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <span className="text-gray-900">
            {row.original.organization || <span className="text-gray-400 italic">Not provided</span>}
          </span>
        </div>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      enableSorting: true,
      cell: ({ row }) => {
        const statusStyles = {
          pending: {
            bg: 'bg-amber-100',
            text: 'text-amber-800',
            border: 'border-amber-200',
            icon: (
              <svg className="w-4 h-4 mr-2 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
          },
          approved: {
            bg: 'bg-green-100',
            text: 'text-green-800',
            border: 'border-green-200',
            icon: (
              <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
          },
          disabled: {
            bg: 'bg-red-100',
            text: 'text-red-800',
            border: 'border-red-200',
            icon: (
              <svg className="w-4 h-4 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
          },

          rejected: {
            bg: 'bg-gray-200',
            text: 'text-gray-800',
            border: 'border-gray-300',
            icon: (
              <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ),
          },
        };

        const style = statusStyles[row.original.status as keyof typeof statusStyles];

        return (
          <div className="flex items-center justify-center">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${style.bg} ${style.text} ${style.border}`}>
              {style.icon}
              {row.original.status.charAt(0).toUpperCase() + row.original.status.slice(1)}
            </span>
          </div>
        );
      },
    },
    {
      header: 'User Type',
      accessorKey: 'userType',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          {row.original.userType ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-gray-100 text-gray-800 border-gray-200">
              {userTypeLabels[row.original.userType] || row.original.userType}
            </span>
          ) : (
            <span className="text-gray-400 italic">Not set</span>
          )}
        </div>
      ),
    },
    {
      header: 'Roles',
      accessorKey: 'roles',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {(row.original.roles || []).map((role) => {
            const roleColors: Record<UserRole, string> = {
              super_admin: 'bg-purple-100 text-purple-800 border-purple-200',
              admin: 'bg-blue-100 text-blue-800 border-blue-200',
              expert: 'bg-indigo-100 text-indigo-800 border-indigo-200',
              user: 'bg-gray-100 text-gray-800 border-gray-200',
            };
            return (
              <span
                key={role}
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${roleColors[role]}`}
              >
                {roleLabels[role]}
              </span>
            );
          })}
        </div>
      ),
    },

    {
      header: 'Actions',
      cell: ({ row }) => {
        const user = row.original;

        return (
          <div className="flex items-center justify-center space-x-2">

            {/* PENDING */}
            {user.status === 'pending' && (
              <>
                <div title='approve'>
                  <Check
                    onClick={() => {
                      setApproveUser(user);
                      setApproveUserType(null);
                    }}
                    className="w-4 h-4 text-blue-600 cursor-pointer"
                  />

                </div>

                <div title='reject'>
                  <X
                    onClick={() => {
                      setRejectUser(user);
                      setRejectReason('');
                    }}
                    className="w-4 h-4 text-red-600 cursor-pointer"
                  />
                </div>
              </>
            )}

            {/* APPROVED */}
            {user.status === 'approved' && (
              <div title='disable'>
                <Ban
                  onClick={() =>
                    updateUserStatus.mutate({ id: user.id, status: 'disabled' })
                  }
                  className="w-4 h-4 text-red-500 cursor-pointer"
                />
              </div>

            )}

            {/* DISABLED */}
            {user.status === 'disabled' && (
              <div title='enable'>
                <Check
                  onClick={() =>
                    updateUserStatus.mutate({ id: user.id, status: 'approved' })
                  }
                  className="w-4 h-4 text-green-600 cursor-pointer"
                />
              </div>



            )}


            {/* EDIT */}
            {canEditRoles && (
              <div title='edit'>
                <Pencil
                  onClick={() => handleEditUser(row.original)}
                  className="w-4 h-4 text-purple-600 cursor-pointer"
                />
              </div>
            )}

            {(user.status === 'approved' || user.status === 'disabled' || user.status === 'rejected') && (
              <div title='delete'> <Trash2 onClick={() => setDeleteUser(user)} className="w-4 h-4 text-red-600 cursor-pointer" /> </div>
            )
            }


          </div>
        );
      }
    },

    {
      header: 'Created At',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>{(() => {
            const date = new Date(row.original.createdAt);
            if (isNaN(date.getTime())) {
              return 'Invalid date';
            }
            return date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
          })()}</span>
        </div>
      ),
    },
  ];

  const filteredUsers = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return statusFilter === 'all'
      ? data
      : data.filter((user) => user.status === statusFilter);
  }, [data, statusFilter]);

  const table = useReactTable({
    data: filteredUsers,
    columns,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  return (
    <MainLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Users Management</h1>
          <div className="flex items-center space-x-4">
            {canCreateUsers && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2.5 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create User
              </button>
            )}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="min-w-[160px] px-4 py-2.5 pr-10 text-base text-gray-900 bg-white border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none hover:border-gray-400 transition-colors duration-200 cursor-pointer"
              >
                <option value="all" className="text-gray-900 bg-white">All Status</option>
                <option value="pending" className="text-gray-900 bg-white">Pending</option>
                <option value="approved" className="text-gray-900 bg-white">Approved</option>
                <option value="disabled" className="text-gray-900 bg-white">Disabled</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* User Type Filter */}
        <div className="relative">
          <select
            value={userTypeFilter}
            onChange={(e) => setUserTypeFilter(e.target.value)}
            className="min-w-[160px] px-4 py-2.5 pr-10 text-base text-gray-900 bg-white border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none hover:border-gray-400 transition-colors duration-200 cursor-pointer"
          >
            <option value="all">All Types</option>
            <option value="employee">Employee</option>
            <option value="distributor">Distributor</option>
            <option value="customer">Customer</option>
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-base text-gray-600">
                  Loading users...
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Top Right Gear Icon */}
              <div className="flex justify-end p-4 border-b border-gray-200">
                <div className="relative" ref={columnMenuRef}>
                  <button
                    onClick={() => setShowColumnMenu(!showColumnMenu)}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50"
                  >
                    <Settings className="w-5 h-5 text-gray-700" />
                  </button>

                  {/* Column Toggle Dropdown */}
                  {showColumnMenu && (
                    <div className="fixed right-8 top-50 w-56 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-[9999]">
                      <p className="text-sm font-semibold text-gray-700 mb-3">
                        Select Columns
                      </p>

                      <div className="space-y-2">
                        {table
                          .getAllColumns()
                          .filter((column) => column.getCanHide())
                          .map((column) => (
                            <label
                              key={column.id}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={column.getIsVisible()}
                                onChange={column.getToggleVisibilityHandler()}
                                className="w-4 h-4"
                              />
                              <span className="text-sm text-gray-700 capitalize">
                                {column.id}
                              </span>
                            </label>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr
                        key={headerGroup.id}
                        className="bg-gray-50 border-b-2 border-gray-200"
                      >
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            scope="col"
                            className="px-4 py-3 text-center align-middle font-semibold w-[170px]"
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="px-4 py-3 text-md text-center align-middle text-gray-900 break-words whitespace-normal"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-between items-center mt-6">
          <div className="flex items-center space-x-3 text-base text-gray-700">
            <span>Show</span>
            <div className="relative">
              <select
                value={table.getState().pagination.pageSize}
                onChange={e => {
                  table.setPageSize(Number(e.target.value))
                }}
                className="min-w-[80px] px-3 py-2 pr-8 text-base text-gray-900 bg-white border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none hover:border-gray-400 transition-colors duration-200 cursor-pointer"
              >
                {[10, 20, 30, 40, 50].map(pageSize => (
                  <option key={pageSize} value={pageSize} className="text-gray-900 bg-white">
                    {pageSize}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <span>entries</span>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-4 py-2.5 text-base font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 transition-colors duration-200"
            >
              Previous
            </button>
            <span className="text-base font-medium text-gray-900">
              Page {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount()}
            </span>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-4 py-2.5 text-base font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 transition-colors duration-200"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Create New User</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormError(null);
                  setCreateFormData({ name: '', organization: '', email: '', password: '', confirmPassword: '', roles: ['user'], userType: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={createFormData.name}
                  onChange={(e) =>
                    setCreateFormData(prev => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization *
                </label>
                <input
                  type="text"
                  value={createFormData.organization}
                  onChange={(e) =>
                    setCreateFormData(prev => ({
                      ...prev,
                      organization: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter organization"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={createFormData.email}
                  onChange={(e) =>
                    setCreateFormData(prev => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  value={createFormData.password}
                  onChange={(e) =>
                    setCreateFormData(prev => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={createFormData.confirmPassword}
                  onChange={(e) =>
                    setCreateFormData(prev => ({
                      ...prev,
                      confirmPassword: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Confirm password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User Type *
                </label>
                <div className="relative">
                  <select
                    value={createFormData.userType}
                    onChange={(e) =>
                      setCreateFormData(prev => ({
                        ...prev,
                        userType: e.target.value as UserType,
                      }))
                    }
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white cursor-pointer"
                  >
                    <option value="" disabled>Select user type</option>
                    {(['employee', 'distributor', 'customer'] as const).map((t) => (
                      <option key={t} value={t}>{userTypeLabels[t]}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Roles *</label>
                <div className="space-y-2">
                  {availableRolesForCreate.map((role) => (
                    <label key={role} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={createFormData.roles.includes(role)}
                        onChange={() => toggleRole(role, true)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{roleLabels[role]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormError(null);
                    setCreateFormData({ name: '', organization: '', email: '', password: '', confirmPassword: '', roles: ['user'], userType: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createUserMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createUserMutation.isPending ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">

            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Edit User</h2>
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setEditingUser(null);
                  setFormError(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {formError}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData(prev => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              {/* Email (read-only: identifies the account, not part of the update payload) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editingUser.email || ''}
                  placeholder="No email on file"
                  disabled
                  readOnly
                  className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                />
                <p className="text-xs text-gray-400 mt-1">Email cannot be changed here.</p>
              </div>

              {/* Organization */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                <input
                  type="text"
                  value={editFormData.organization}
                  onChange={(e) =>
                    setEditFormData(prev => ({ ...prev, organization: e.target.value }))
                  }
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              {/* User Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
                <div className="relative">
                  <select
                    value={editFormData.userType}
                    onChange={(e) =>
                      setEditFormData(prev => ({ ...prev, userType: e.target.value as UserType }))
                    }
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white cursor-pointer"
                  >
                    {(['employee', 'distributor', 'customer'] as const).map((t) => (
                      <option key={t} value={t}>{userTypeLabels[t]}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Roles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Roles *</label>
                <div className="space-y-2">
                  {availableRolesForEdit.map(role => (
                    <label key={role} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editFormData.roles.includes(role)}
                        onChange={() => toggleRole(role, false)}
                      />
                      <span className="ml-2">{roleLabels[role]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowEditUserModal(false);
                    setEditingUser(null);
                    setFormError(null);
                    setEditFormData({ name: '', organization: '', roles: [], userType: 'customer' });
                  }}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={updateUserMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                >
                  {updateUserMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve User Modal */}

      {approveUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Approve user</h2>
                <p className="text-sm text-gray-500 mt-1">{approveUser.email}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setApproveUser(null);
                  setApproveUserType(null);
                  setApproveModalError(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {approveModalError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {approveModalError}
                </div>
              )}

              <div>
                <p className="block text-sm text-gray-700 font-bold mb-2">User type </p>
                <div className="space-y-2">
                  {(['employee', 'distributor', 'customer'] as const).map((t) => (
                    <label key={t} className="flex items-center">
                      <input
                        type="radio"
                        name="approveUserType"
                        checked={approveUserType === t}
                        onChange={() => {
                          setApproveUserType(t);
                          setApproveModalError(null);
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{userTypeLabels[t]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setApproveUser(null);
                    setApproveUserType(null);
                    setApproveModalError(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmApprove}
                  disabled={updateUserStatus.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateUserStatus.isPending ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rejectUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">

            <h2 className="text-lg font-bold mb-4">Reject User</h2>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason"
              className="w-full border rounded p-2 mb-4"
            />

            {!rejectReason.trim() && (
              <p className="text-sm text-red-500 mb-2">Reason is required</p>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={() => setRejectUser(null)}>
                Cancel
              </button>

              <button
                disabled={!rejectReason.trim() || updateUserStatus.isPending}
                onClick={() => {
                  updateUserStatus.mutate({
                    id: rejectUser.id,
                    status: 'rejected',
                    reason: rejectReason,
                  });

                  setRejectUser(null);
                }}
                className="bg-red-600 text-white px-4 py-2 rounded"
              >
                {updateUserStatus.isPending ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">

            <h2 className="text-lg font-bold  mb-4">
              Are you sure you want to delete this user ?
            </h2>

            <div className="flex justify-end gap-3">
              <button className='px-4 py-2' onClick={() => setDeleteUser(null)}>
                Cancel
              </button>

              <button
                disabled={deleteUserMutation.isPending}
                onClick={() => deleteUserMutation.mutate(deleteUser.id)}
                className="bg-red-600 text-white px-4 py-2 rounded"
              >
                {deleteUserMutation.isPending ? 'Deleting...' : 'Delete'}

              </button>
            </div>
          </div>
        </div>
      )}

    </MainLayout>
  );
};